/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { UnknownAction } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { EventChannel, Task } from 'redux-saga';
import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  PutEffect,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import {
  directionalGameLog,
  gameLogger,
  selectIsHistoryMode,
  selectIsPreGameState,
  selectPlayerId,
  summarizeControllerContext,
} from '~/app';
import {
  extractHurdlesFromLanes,
  getNextAnimationSequenceId,
  Horse as StateHorse,
  HorseField,
  setHorseField,
  setHorses,
  setHorsesField,
  setHurdles,
  setMoves,
} from '~/board';
import {
  setCardDice,
  setEventLog,
  setHandiFailureHorseIndex,
  setHorseCards,
  setHorsesBonusCards,
  setHorsesCards,
  setLastCard,
  setLastCardHorseIndex,
  setLastTurnDicePlayerId,
  setLastTurnDices,
  setLastTurnDicesCur,
  setLatestGameStateAnchor,
  setOwnHorseRemovedCards,
} from '~/overlay';
import { buildReplayHorsePaths, type ReplayHorsePathResult } from '~/replay/horsePathReplay';
import type { GameStateAtEvent } from '~/replay/reducers';
import { computeStateAtEventIndex } from '~/replay/reducers';
import { EventLogEntry } from '~/server/src/dtos';
import { Controller } from '~/server/src/interfaces';
import Turfmaster, { Board, Field, Game, Horse, Move, Player } from '~/server/src/tmmodel';
import { actions as setupActions } from '~/setup';
import type { SetupChannelEvent } from '~/setup/channelEvent';

import {
  leaveAcked,
  leaveFailed,
  setCurrentState,
  setHorsesBehindGoal,
  setHorsesDropped,
  setHorsesFinished,
  setHorsesHandi,
  setHorsesPoints,
  setHorsesRanks,
  setHorsesStarted,
  setPlayers,
  setRound,
  setTurnDicePlayerId,
  setTurnDices,
  setTurnDicesCur,
  setTurnOrder,
  setTurnPos,
  setTurnType,
  triggerRejoinGame,
} from './actions';
import calculateClientHorseColors from './calculateClientHorseColors';
import sagaWatchdog from './sagaWatchdog';

/**
 * Convert a Game instance to a plain serializable object for Redux store.
 * Redux state must be serializable; class instances trigger the non-serializable value warning.
 */
function gameToSerializable(game: Game): Record<string, unknown> {
  return JSON.parse(JSON.stringify(game)) as Record<string, unknown>;
}

export const createMoveUpdateAction = (moves: Move[]): PutEffect =>
  put(
    setMoves(
      moves.map((move) => {
        return move.fields.map((field) => {
          return { x: field.x, y: field.y };
        });
      }),
    ),
  );

export const createHorsesCardsUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesCards(
      horses.map((horse) => {
        return horse.cards;
      }),
    ),
  );

export const createHorsesRanksUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesRanks(
      horses.map(({ rank, totalrank }) => {
        return { rank, totalRank: totalrank };
      }),
    ),
  );

export const createHorsesHandiUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesHandi(
      horses.map((horse) => {
        return horse.handi;
      }),
    ),
  );

export const createHorsesPointsUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesPoints(
      horses.map((horse) => {
        return horse.points;
      }),
    ),
  );

export const createHorsesBonusCardsUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesBonusCards(
      horses.map((horse) => {
        return horse.bonuscards;
      }),
    ),
  );

export const createHorsesFinishedUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesFinished(
      horses.map((horse) => {
        return horse.finished;
      }),
    ),
  );

export const createHorsesDroppedUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesDropped(
      horses.map((horse) => {
        return horse.dropped;
      }),
    ),
  );

export const createHorsesStartedUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesStarted(
      horses.map((horse) => {
        if (horse && horse.field) return horse.field.pos > 0;
        else return horse.dropped || horse.finished;
      }),
    ),
  );

export const createHorsesBehindGoalUpdateAction = (horses: Horse[]): PutEffect =>
  put(
    setHorsesBehindGoal(
      horses.map((horse) => {
        if (horse && horse.field) return horse.field.pos > 18;
        else return horse.dropped || horse.finished;
      }),
    ),
  );

export const createPlayersUpdateAction = (players: Player[], horses: Horse[]): PutEffect =>
  put(
    setPlayers(
      players.map((player, index) => {
        return {
          id: index,
          userId: player.userid,
          name: player.name,
          type: player.type,
          cardType: (
            horses.find((h) => h.playerid === index) || { cardType: Turfmaster.cardTypes.BRONZE }
          ).cardType,
          totalRank: player.totalrank,
          playerName: player.name,
          country: player.country,
        };
      }),
    ),
  );

export const createInitialGameUpdateAction = (game: Game): PutEffect[] => [
  put(setupActions.setHorseAmount(game.horseamount)),
  put(setupActions.setGameName(game.name || '')),
  put(setRound(game.round)),
  put(setTurnPos(game.turnpos)),
  put(setTurnOrder(game.turnorder)),
  put(setTurnDices(game.turndices)),
  put(setTurnDicesCur(game.turndicescur)),
  put(setTurnType(game.turntype)),
  put(setTurnDicePlayerId(game.turndiceplayerid)),
  createPlayersUpdateAction(game.players, game.horses),
  createHorsesCardsUpdateAction(game.horses),
  createHorsesRanksUpdateAction(game.horses),
  createHorsesHandiUpdateAction(game.horses),
  createHorsesPointsUpdateAction(game.horses),
  createHorsesBonusCardsUpdateAction(game.horses),
  createHorsesFinishedUpdateAction(game.horses),
  createHorsesDroppedUpdateAction(game.horses),
  createHorsesStartedUpdateAction(game.horses),
  createHorsesBehindGoalUpdateAction(game.horses),
];

export const createHurdlesUpdateAction = (lanes: Field[][]): PutEffect => {
  return put(setHurdles(extractHurdlesFromLanes(lanes)));
};

export const createHorsesUpdateAction = (horses: Horse[], clientHorseColors: number[]): PutEffect =>
  put(
    setHorses(
      horses.map<StateHorse>((horse, index) => {
        return {
          id: horse.horseid,
          index,
          playerId: horse.playerid,
          color: clientHorseColors[index],
          jockeyName: horse.jockeyName,
          realColor: horse.color,
          name: horse.name,
          nextTimeoutDateString: undefined, // TODO: map nextTimeoutDateString from controller
        };
      }),
    ),
  );

export const createHorsesFieldUpdateAction = (
  horses: Horse[],
  _options?: { forReplay?: boolean },
): PutEffect | undefined => {
  const seqId = getNextAnimationSequenceId();
  const horsesWithFieldCount = horses.filter((horse) => !!horse.field).length;
  if (horsesWithFieldCount === 0) {
    return undefined;
  }
  return put(
    setHorsesField(
      horses.map(({ field }) => {
        const x = field?.x ?? 0;
        const y = field?.y ?? 0;
        return {
          x,
          y,
          pos: field?.pos ?? 0,
          prog: field?.prog ?? 0,
          animationIntent: {
            mode: 'teleport' as const,
            speedProfile: 'instant',
            sequenceId: seqId,
            path: [{ x, y }],
          },
        };
      }),
    ),
  );
};

export const createHorseFieldUpdateAction = (
  index: number,
  horse: Horse,
  move?: Move,
): PutEffect => {
  const x = horse.field?.x ?? 0;
  const y = horse.field?.y ?? 0;
  const seqId = getNextAnimationSequenceId();
  const fields = move?.fields ?? [];
  const hasPath = fields.length > 0;
  const animationIntent = hasPath
    ? {
        mode: 'live' as const,
        speedProfile: 'live',
        sequenceId: seqId,
        path: fields.map((f) => ({ x: f.x, y: f.y })),
      }
    : {
        mode: 'teleport' as const,
        speedProfile: 'instant',
        sequenceId: seqId,
        path: [{ x, y }],
      };
  return put(
    setHorseField({
      index,
      value: {
        x,
        y,
        pos: horse.field?.pos ?? 0,
        prog: horse.field?.prog ?? 0,
        animationIntent,
      },
    }),
  );
};

export const createInitialBoardUpdateAction = (
  game: Game,
  board: Board,
  clientHorseColors: number[],
  options?: { forReplay?: boolean },
): PutEffect[] => {
  const baseEffects: PutEffect[] = [
    createHurdlesUpdateAction(board.fields),
    createHorsesUpdateAction(game.horses, clientHorseColors),
  ];
  const hasAnyHorseField = game.horses.some((horse) => !!horse.field);
  if (!hasAnyHorseField) {
    return baseEffects;
  }
  const horsesFieldEffect = createHorsesFieldUpdateAction(game.horses, options);
  return horsesFieldEffect ? [...baseEffects, horsesFieldEffect] : baseEffects;
};

/**
 * Pass a copy so Redux/Immer never freezes the controller's eventLog.
 * Immer freezes any plain array that ends up in state; mutating that same ref elsewhere then fails.
 * @see https://immerjs.github.io/immer/freezing/
 */
export const createInitialOverlayUpdateAction = (eventLog: EventLogEntry[]): PutEffect[] => [
  put(setEventLog([...eventLog])),
];

/**
 * Game-like state: either a live Game from the controller or replay-derived state
 * (same shape at runtime from computeStateAtEventIndex).
 */
export type ReplayGameLike = Game | Record<string, unknown>;

export interface ReplaySyncPathOptions {
  eventLog: EventLogEntry[];
  anchorState: GameStateAtEvent;
  fromEventIndex: number;
  toEventIndex: number;
  sequenceId: number;
}

/**
 * Builds HorseField[] for replay sync: every horse gets an explicit animationIntent
 * (history for multi-point paths, teleport for single-point or no path).
 */
function buildHorseFieldsWithReplayPaths(
  stateAtTo: ReplayGameLike,
  pathResults: ReplayHorsePathResult[],
  sequenceId: number,
): HorseField[] {
  const gameLike = stateAtTo as Game;
  const horses = gameLike.horses ?? [];
  const byIndex = new Map(pathResults.map((r) => [r.horseIndex, r]));

  return horses.map((horse, index) => {
    const field = horse.field;
    const x = field?.x ?? 0;
    const y = field?.y ?? 0;
    const pos = field?.pos ?? 0;
    const prog = field?.prog ?? 0;
    const result = byIndex.get(index);
    if (result && result.path.length > 1) {
      const last = result.path[result.path.length - 1];
      return {
        x: last.x,
        y: last.y,
        pos,
        prog,
        animationIntent: {
          mode: 'history' as const,
          speedProfile: result.speedProfile,
          sequenceId: result.sequenceId,
          path: result.path,
        },
      } as HorseField;
    }
    const target = result?.path?.[0] ?? { x, y };
    return {
      x: target.x,
      y: target.y,
      pos,
      prog,
      animationIntent: {
        mode: 'teleport' as const,
        speedProfile: 'instant',
        sequenceId,
        path: [target],
      },
    } as HorseField;
  });
}

/**
 * Returns plain Redux actions to sync game and board state (e.g. for replay mode).
 * Dispatch with batch() to update store from derived snapshot state.
 * Accepts Game or replay-derived state (ReplayGameLike); both have the same shape at runtime.
 * When pathOptions is provided (history navigation), builds per-horse movement paths and
 * emits setHorsesField with animation intent for history jump animation.
 */
export function getReplaySyncActions(
  game: ReplayGameLike,
  board: Board,
  clientHorseColors: number[],
  pathOptions?: ReplaySyncPathOptions,
): UnknownAction[] {
  const gameLike = game as Game;
  const gameEffects = createInitialGameUpdateAction(gameLike);
  const gameRecord = gameLike as unknown as Record<string, unknown>;
  const boardId = gameRecord.boardid ?? gameRecord.boardId;
  const boardIdNum = typeof boardId === 'number' ? boardId : 0;
  const boardInstance = board ?? new Board(boardIdNum);

  if (pathOptions) {
    const { eventLog, anchorState, fromEventIndex, toEventIndex, sequenceId } = pathOptions;
    // For backward navigation (fromEventIndex > toEventIndex), we're navigating FROM the current position
    // (fromEventIndex) TO the target position (toEventIndex). Use fromEventIndex as the starting state.
    // However, if fromEventIndex is stale (e.g., 0 when we're actually at event 25), we need to detect this.
    // When navigating backward from live mode, fromEventIndex might be stale (0) while toEventIndex is the current position (25).
    // In this case, use toEventIndex or eventLog.length as the starting state.
    // If fromEventIndex === toEventIndex, this is a no-op (staying at the same event).
    // Skip path generation and use the state at toEventIndex directly (game parameter is already state at toEventIndex).
    if (fromEventIndex === toEventIndex) {
      const horseFields = buildHorseFieldsWithReplayPaths(game, [], sequenceId);
      const lanes = boardInstance.fields ?? board?.fields ?? [];
      const boardEffects = [
        put(setHurdles(lanes.length ? extractHurdlesFromLanes(lanes) : [])),
        put(setHorsesField(horseFields)),
      ];
      return [
        ...gameEffects.map((e) => e.payload.action as UnknownAction),
        ...boardEffects.map((e) => e.payload.action as UnknownAction),
      ];
    }
    // For backward navigation, use fromEventIndex as the starting state (it should be correct from HistoryPanel).
    // For forward navigation, use fromEventIndex as the starting state (don't use eventLog.length - that's wrong).
    // Only fix stale fromEventIndex when it's clearly wrong (much smaller than toEventIndex in forward nav).
    const actualFromEventIndex = fromEventIndex;
    const stateAtFrom = computeStateAtEventIndex(anchorState, eventLog, actualFromEventIndex);
    const pathResults = buildReplayHorsePaths(
      eventLog,
      fromEventIndex,
      toEventIndex,
      stateAtFrom as GameStateAtEvent,
      sequenceId,
      game as GameStateAtEvent,
    );
    // Collapse only true no-op loops (path has ≤2 points and start === end) so Horse teleports.
    // Do not collapse long paths that happen to have first ≈ last (e.g. floating point); animate them.
    const sanitizedPathResults = pathResults.map((r) => {
      if (r.path.length <= 1) return r;
      const first = r.path[0];
      const last = r.path[r.path.length - 1];
      const isLoop = Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6;
      if (isLoop && r.path.length <= 2) return { ...r, path: [last] };
      return r;
    });
    const horseFields = buildHorseFieldsWithReplayPaths(game, sanitizedPathResults, sequenceId);
    const lanes = boardInstance.fields ?? board?.fields ?? [];
    const boardEffects = [
      put(setHurdles(lanes.length ? extractHurdlesFromLanes(lanes) : [])),
      put(setHorsesField(horseFields)),
    ];
    return [
      ...gameEffects.map((e) => e.payload.action as UnknownAction),
      ...boardEffects.map((e) => e.payload.action as UnknownAction),
    ];
  }

  const boardEffects = createInitialBoardUpdateAction(gameLike, boardInstance, clientHorseColors, {
    forReplay: true,
  });
  return [
    ...gameEffects.map((e) => e.payload.action as UnknownAction),
    ...boardEffects.map((e) => e.payload.action as UnknownAction),
  ];
}

function* sagaGameReceiver(
  controller: Controller,
  channel: EventChannel<SetupChannelEvent>,
  initialEvents: SetupChannelEvent[] = [],
) {
  const game = controller.getGame();
  const board = controller.getBoard();

  // only run game receiver if all needed dependencies are valid
  if (!controller || !channel || !game || !board) return;

  const ownPlayerId: number = yield select(selectPlayerId);
  const clientHorseColors = calculateClientHorseColors(game.horses, game.players, ownPlayerId);

  // initial game update (includes currentState and eventLog so spectator reconnect shows correct status)
  const eventLog = controller.getEventLog() ?? [];
  yield all([
    ...createInitialGameUpdateAction(game),
    ...createInitialBoardUpdateAction(game, board, clientHorseColors),
    ...createInitialOverlayUpdateAction(eventLog),
    put(setCurrentState(game.currentState)),
    put(setLatestGameStateAnchor(gameToSerializable(game))),
  ]);

  // Reconciliation:
  // During join/start handshakes, socket events can arrive while this saga is starting.
  // If the controller already has a started round but Redux is still in a pre-game state,
  // resync game + board once to avoid leaving horses on fallback pregame coordinates.
  yield delay(0);
  const controllerGameAfter = controller.getGame();
  const controllerBoardAfter = controller.getBoard();
  if (controllerGameAfter && controllerBoardAfter && controllerGameAfter.round > 0) {
    const reduxIsPreGame = yield select(selectIsPreGameState);
    const reduxHorseFieldsLength = yield select(
      (s: { board?: { horseFields?: unknown[] } }) => s.board?.horseFields?.length ?? 0,
    );
    if (reduxIsPreGame || reduxHorseFieldsLength === 0) {
      const updatedClientHorseColors = calculateClientHorseColors(
        controllerGameAfter.horses,
        controllerGameAfter.players,
        ownPlayerId,
      );
      yield all([
        ...createInitialGameUpdateAction(controllerGameAfter),
        ...createInitialBoardUpdateAction(
          controllerGameAfter,
          controllerBoardAfter,
          updatedClientHorseColors,
        ),
        ...createInitialOverlayUpdateAction(eventLog),
        put(setCurrentState(controllerGameAfter.currentState)),
        put(setLatestGameStateAnchor(gameToSerializable(controllerGameAfter))),
      ]);
    }
  }

  // Fork saga to handle triggerRejoinGame action - reuse REJOINGAME logic when returning to live from history
  yield fork(function* () {
    yield takeEvery(triggerRejoinGame.type, function* () {
      try {
        const localController = controller as unknown as { rejoinGame?: () => Promise<void> };
        if (localController && typeof localController.rejoinGame === 'function') {
          yield call(localController.rejoinGame);
        }
      } catch (error) {
        gameLogger.error({ err: error }, 'error triggering rejoin game');
      }
    });
  });

  // receive and handle ingame messages from controller
  const bufferedInitialEvents: SetupChannelEvent[] = [...initialEvents];
  while (true) {
    const watchdog: Task = yield fork(sagaWatchdog);

    // wait for next message of controller
    const event: SetupChannelEvent =
      bufferedInitialEvents.length > 0
        ? bufferedInitialEvents.shift()!
        : ((yield take(channel)) as SetupChannelEvent);
    const { state, params } = event;

    yield cancel(watchdog);

    try {
      const game = controller.getGame();
      const board = controller.getBoard();
      const eventLog = controller.getEventLog();

      const currentPlayerId =
        game.horses && game.turnorder && game.turnorder.length
          ? (controller.getPlayerId() as number)
          : -1;
      const currentHorseIndex = controller.getHorseId() as number;
      const playerId: number = yield select(selectPlayerId);
      // Some unit tests provide a minimal Redux state without `game`.
      // Read previous turn type defensively; fall back to controller snapshot when absent.
      const prevTurnType: number | undefined = yield select(
        (s: { game?: { turntype?: number } }) =>
          typeof s.game?.turntype === 'number' ? s.game.turntype : undefined,
      );

      // TODO: extract chat to other receiver
      const batchedEffects: PutEffect[] = [];
      if (
        state === Turfmaster.stateMachine.JOINGAME ||
        state === Turfmaster.stateMachine.JOINGAMEOPP ||
        state === Turfmaster.stateMachine.LEAVEGAMEOPP ||
        state === Turfmaster.stateMachine.STARTGAME ||
        state === Turfmaster.stateMachine.STARTROUND ||
        state === Turfmaster.stateMachine.REJOINGAME
      ) {
        // Reserved for chat-related handling; see TODO above.
      }
      // Lobby polling responses must not overwrite the "game running" boundary.
      // Otherwise the horse component may temporarily render with pre-game layout/logic.
      const shouldSetCurrentStateFromEvent =
        state !== Turfmaster.stateMachine.NEWMESSAGE &&
        state !== Turfmaster.stateMachine.GETGAMES &&
        state !== Turfmaster.stateMachine.GETTOURNAMENTS &&
        state !== Turfmaster.stateMachine.REJOINGAME &&
        state !== Turfmaster.stateMachine.RESTOREGAME;
      // Do not dispatch setCurrentState before the switch: append it after board/field updates
      // so Horse never observes STARTGAME/STARTROUND with empty lane coordinates.

      // react to controller message with e.g. firing the corresponding redux action
      switch (state) {
        case Turfmaster.stateMachine.RESTOREGAME: {
          // RESTOREGAME is used as a lightweight probe (e.g. start/menu screens) and may arrive
          // on the shared controller channel. It is handled by setup sagas; ignore here to avoid
          // misleading "unknown controller event" warnings.
          directionalGameLog.inbound(
            'RESTOREGAME',
            {
              phase: 'ignored',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          break;
        }
        case Turfmaster.stateMachine.GETGAMES: {
          // Pre-game lobby state; handled by setup sagas. Ignore here to avoid noisy UNKNOWN GAME STATE warnings.
          directionalGameLog.inbound(
            'GETGAMES',
            {
              phase: 'ignored',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          break;
        }
        case Turfmaster.stateMachine.GETTOURNAMENTS: {
          // Pre-game lobby state; handled by setup sagas. Ignore here to avoid noisy UNKNOWN GAME STATE warnings.
          directionalGameLog.inbound(
            'GETTOURNAMENTS',
            {
              phase: 'ignored',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          break;
        }
        case Turfmaster.stateMachine.NEWGAME:
        case Turfmaster.stateMachine.JOINGAME: {
          directionalGameLog.inbound(
            state === Turfmaster.stateMachine.NEWGAME ? 'NEWGAME' : 'JOINGAME',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );

          // These can re-arrive on transient socket reconnects after the initial setup
          // sagas have already finished listening. Initialize Redux so horse positions and
          // overlay (waiting-for-players / pre-round) are consistent.
          const clientHorseColors = calculateClientHorseColors(
            game.horses,
            game.players,
            ownPlayerId,
          );
          batchedEffects.push(
            ...createInitialGameUpdateAction(game),
            ...createInitialBoardUpdateAction(game, board, clientHorseColors),
            ...createInitialOverlayUpdateAction(eventLog),
            put(setLatestGameStateAnchor(gameToSerializable(game))),
          );
          // Rejoin safety: if we are already past first dice turn, never keep a stale last card.
          if (game.turntype === Turfmaster.turntypes.DICES && game.turnpos > 0) {
            batchedEffects.push(put(setLastCardHorseIndex(-1)));
            batchedEffects.push(put(setLastCard(-1)));
          }
          break;
        }
        case Turfmaster.stateMachine.REJOINGAME: {
          // Reconnect/resync path: the controller restores game + board and forwards
          // a REJOINGAME controller message to this receiver.
          directionalGameLog.inbound(
            'REJOINGAME',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );

          // The controller message type is `REJOINGAME`, but the UI expects the
          // real in-game phase from `game.currentState` (e.g. STARTGAME/STARTROUND).
          // If we already have a started game in Redux, ignore only truly pre-game
          // reconnect snapshots to prevent resetting horses. `round === 0` alone is
          // not enough because active races can still be in round 0 with in-game state.
          const reduxIsPreGame = yield select(selectIsPreGameState);
          const controllerIsPreGame =
            game.currentState === Turfmaster.stateMachine.NEWGAME ||
            game.currentState === Turfmaster.stateMachine.JOINGAME ||
            game.currentState === Turfmaster.stateMachine.LEAVEGAME ||
            game.currentState === Turfmaster.stateMachine.JOINGAMEOPP ||
            game.currentState === Turfmaster.stateMachine.LEAVEGAMEOPP;
          const ignoreStalePreGameRejoin =
            !reduxIsPreGame && game.round === 0 && controllerIsPreGame;
          if (ignoreStalePreGameRejoin) {
            directionalGameLog.inbound(
              'REJOINGAME (ignored stale pre-game snapshot)',
              {
                phase: 'ignored',
                payloadSummary: summarizeControllerContext({
                  controller,
                  state,
                  params,
                  game,
                  board,
                }),
              },
              'controller event',
            );
            break;
          }

          batchedEffects.push(put(setCurrentState(game.currentState)));

          const clientHorseColors = calculateClientHorseColors(
            game.horses,
            game.players,
            ownPlayerId,
          );
          batchedEffects.push(
            ...createInitialGameUpdateAction(game),
            ...createInitialBoardUpdateAction(game, board, clientHorseColors),
            ...createInitialOverlayUpdateAction(eventLog),
            put(setLatestGameStateAnchor(gameToSerializable(game))),
          );
          break;
        }
        case Turfmaster.stateMachine.LEAVEGAME: {
          directionalGameLog.inbound(
            'LEAVEGAME',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          const leaveErrorCode =
            params && typeof (params as { error?: unknown }).error === 'string'
              ? ((params as { error: string }).error ?? '').trim()
              : '';
          if (leaveErrorCode) {
            batchedEffects.push(
              put(
                leaveFailed({
                  reason: leaveErrorCode === 'leave_not_allowed' ? 'controller' : 'network',
                  message: leaveErrorCode,
                }),
              ),
            );
            break;
          }

          batchedEffects.push(put(leaveAcked()));
          batchedEffects.push(createPlayersUpdateAction(game.players, game.horses));
          const clientHorseColorsLeave = calculateClientHorseColors(
            game.horses,
            game.players,
            ownPlayerId,
          );
          batchedEffects.push(createHorsesUpdateAction(game.horses, clientHorseColorsLeave));
          batchedEffects.push(put(setEventLog([...eventLog])));
          if (game.round > 0) {
            const horsesFieldEffect = createHorsesFieldUpdateAction(game.horses);
            if (horsesFieldEffect) batchedEffects.push(horsesFieldEffect);
          }
          break;
        }
        case Turfmaster.stateMachine.JOINGAMEOPP: {
          directionalGameLog.inbound(
            'JOINGAMEOPP',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(createPlayersUpdateAction(game.players, game.horses));
          const clientHorseColors = calculateClientHorseColors(
            game.horses,
            game.players,
            ownPlayerId,
          );
          batchedEffects.push(createHorsesUpdateAction(game.horses, clientHorseColors));
          batchedEffects.push(put(setEventLog([...eventLog])));
          // Don't set field positions in lobby - let CSS formula handle it
          // (CSS formula in Horse.tsx already positions based on index/horseCount)
          break;
        }
        case Turfmaster.stateMachine.LEAVEGAMEOPP: {
          directionalGameLog.inbound(
            'LEAVEGAMEOPP',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(createPlayersUpdateAction(game.players, game.horses));
          const clientHorseColors = calculateClientHorseColors(
            game.horses,
            game.players,
            ownPlayerId,
          );
          batchedEffects.push(createHorsesUpdateAction(game.horses, clientHorseColors));
          batchedEffects.push(put(setEventLog([...eventLog])));
          // Only update field positions if game has started (horses have positions)
          if (game.round > 0) {
            const horsesFieldEffect = createHorsesFieldUpdateAction(game.horses);
            if (horsesFieldEffect) batchedEffects.push(horsesFieldEffect);
          }
          break;
        }
        case Turfmaster.stateMachine.STARTGAME:
          {
            directionalGameLog.inbound(
              'STARTGAME',
              {
                phase: 'response',
                payloadSummary: summarizeControllerContext({
                  controller,
                  state,
                  params,
                  game,
                  board,
                }),
              },
              'controller event',
            );
            const clientHorseColors = calculateClientHorseColors(
              game.horses,
              game.players,
              ownPlayerId,
            );
            batchedEffects.push(
              ...createInitialGameUpdateAction(game),
              ...createInitialBoardUpdateAction(game, board, clientHorseColors),
            );
            // set last dices to match the virtually last player without a value
            batchedEffects.push(put(setLastTurnDicePlayerId(game.turndiceplayerid)));
            batchedEffects.push(put(setLastTurnDices([-1, -1])));
            batchedEffects.push(put(setEventLog([...eventLog])));
            // spectator join sends currentState in params so status bar shows correct phase
            if (params?.currentState != null) {
              batchedEffects.push(put(setCurrentState(params.currentState)));
            }
          }
          break;
        case Turfmaster.stateMachine.STARTROUND:
          directionalGameLog.inbound(
            'STARTROUND',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          {
            const horsesFieldEffect = createHorsesFieldUpdateAction(game.horses);
            if (horsesFieldEffect) batchedEffects.push(horsesFieldEffect);
          }
          batchedEffects.push(createHorsesRanksUpdateAction(game.horses));
          batchedEffects.push(createPlayersUpdateAction(game.players, game.horses));
          batchedEffects.push(createHorsesHandiUpdateAction(game.horses));
          batchedEffects.push(createHorsesFinishedUpdateAction(game.horses));
          batchedEffects.push(createHorsesDroppedUpdateAction(game.horses));
          batchedEffects.push(createHorsesStartedUpdateAction(game.horses));
          batchedEffects.push(createHorsesBehindGoalUpdateAction(game.horses));
          batchedEffects.push(put(setTurnOrder(cloneDeep(game.turnorder))));
          batchedEffects.push(put(setTurnPos(game.turnpos)));
          batchedEffects.push(put(setRound(game.round)));
          batchedEffects.push(put(setTurnType(game.turntype)));
          batchedEffects.push(put(setEventLog([...eventLog])));
          break;
        case Turfmaster.stateMachine.MERGECARDS:
          directionalGameLog.inbound(
            'MERGECARDS',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          game.horses.forEach((horse, index) => {
            if (horse.playerid === playerId)
              batchedEffects.push(put(setHorseCards({ index: index, value: horse.cards })));
          });
          batchedEffects.push(put(setTurnPos(game.turnpos)));
          if (playerId === currentPlayerId)
            batchedEffects.push(put(setOwnHorseRemovedCards(params.removedCards || [])));
          else batchedEffects.push(put(setOwnHorseRemovedCards([])));
          break;
        case Turfmaster.stateMachine.MERGECARDSOPP:
          directionalGameLog.inbound(
            'MERGECARDSOPP',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(createHorsesBonusCardsUpdateAction(game.horses));
          game.horses.forEach((horse, index) => {
            if (horse.playerid === playerId)
              batchedEffects.push(put(setHorseCards({ index: index, value: horse.cards })));
          });
          if (playerId !== currentPlayerId) batchedEffects.push(put(setOwnHorseRemovedCards([])));
          batchedEffects.push(put(setEventLog([...eventLog])));
          break;
        case Turfmaster.stateMachine.STARTTURN:
          directionalGameLog.inbound(
            'STARTTURN',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(createHorsesRanksUpdateAction(game.horses));
          batchedEffects.push(createPlayersUpdateAction(game.players, game.horses));
          batchedEffects.push(createHorsesHandiUpdateAction(game.horses));
          batchedEffects.push(put(setTurnOrder(cloneDeep(game.turnorder))));
          batchedEffects.push(put(setTurnPos(game.turnpos)));
          batchedEffects.push(put(setTurnType(game.turntype)));
          batchedEffects.push(put(setTurnDicePlayerId(game.turndiceplayerid)));
          batchedEffects.push(put(setEventLog([...eventLog])));
          break;
        case Turfmaster.stateMachine.ADDBONUSCARDSOPP: {
          directionalGameLog.inbound(
            'ADDBONUSCARDSOPP',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          const horseId = (params.horseid as number) || 0;
          const horse = game.horses[horseId];
          batchedEffects.push(createHorsesBonusCardsUpdateAction(game.horses));
          if (horse && horse.playerid === playerId) {
            batchedEffects.push(put(setHorseCards({ index: horseId, value: horse.cards })));
          }
          batchedEffects.push(put(setEventLog([...eventLog])));
          break;
        }
        case Turfmaster.stateMachine.SELECTCARDDICE:
          directionalGameLog.inbound(
            'SELECTCARDDICE',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(put(setTurnPos(game.turnpos)));
          batchedEffects.push(put(setTurnOrder(cloneDeep(game.turnorder))));
          // if same card is clicked we are sending out SELECTCARDDICE with -1 and the server
          // does not respond with SELECTMOVE but with an empty SELECTCARDDICE
          if (game.turntype === Turfmaster.turntypes.CARDS && currentPlayerId === playerId)
            batchedEffects.push(createMoveUpdateAction([]));
          break;
        case Turfmaster.stateMachine.SELECTMOVE:
          directionalGameLog.inbound(
            'SELECTMOVE',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(put(setCardDice(params.carddice)));
          batchedEffects.push(put(setTurnPos(game.turnpos)));
          batchedEffects.push(put(setTurnOrder(cloneDeep(game.turnorder))));
          if (game.turntype === Turfmaster.turntypes.CARDS)
            batchedEffects.push(put(setHandiFailureHorseIndex(-1)));
          if (currentPlayerId === playerId)
            batchedEffects.push(createMoveUpdateAction((params.moves as Move[]) || []));
          break;
        case Turfmaster.stateMachine.SELECTMOVEOPP: {
          directionalGameLog.inbound(
            'SELECTMOVEOPP',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(createHorsesDroppedUpdateAction(game.horses));
          batchedEffects.push(createHorsesFinishedUpdateAction(game.horses));
          batchedEffects.push(createHorsesStartedUpdateAction(game.horses));
          batchedEffects.push(createHorsesBehindGoalUpdateAction(game.horses));
          batchedEffects.push(
            createHorseFieldUpdateAction(
              currentHorseIndex,
              game.horses[currentHorseIndex],
              params.move,
            ),
          );
          batchedEffects.push(put(setMoves([])));
          batchedEffects.push(put(setCardDice(params.carddice)));

          if (game.turntype === Turfmaster.turntypes.CARDS) {
            batchedEffects.push(
              put(
                setHorseCards({
                  index: currentHorseIndex,
                  value: game.horses[currentHorseIndex].cards,
                }),
              ),
            );
            batchedEffects.push(put(setLastCardHorseIndex(game.turnorder[game.turnpos])));
            batchedEffects.push(put(setLastCard(params.carddice)));
            // Use event payload for handicap toast; fall back to currentHorseIndex if horseid missing (e.g. replay)
            const handiHorseIndex =
              params.handicapExceeded === true
                ? ((params.horseid as number | undefined) ?? currentHorseIndex)
                : -1;
            batchedEffects.push(put(setHandiFailureHorseIndex(handiHorseIndex)));
          }

          break;
        }
        case Turfmaster.stateMachine.SELECTDICES:
          directionalGameLog.inbound(
            'SELECTDICES',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(put(setTurnDices(game.turndices)));
          break;
        case Turfmaster.stateMachine.SELECTDICESOPP:
          directionalGameLog.inbound(
            'SELECTDICESOPP',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(put(setTurnDicesCur(game.turndicescur)));
          batchedEffects.push(put(setLastTurnDicePlayerId(game.turndiceplayerid)));
          batchedEffects.push(put(setLastTurnDices(game.turndices)));
          batchedEffects.push(put(setLastTurnDicesCur(game.turndicescur)));
          break;
        case Turfmaster.stateMachine.ENDTURN: {
          directionalGameLog.inbound(
            'ENDTURN',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(createHorsesRanksUpdateAction(game.horses));
          batchedEffects.push(createPlayersUpdateAction(game.players, game.horses));
          batchedEffects.push(createHorsesHandiUpdateAction(game.horses));
          batchedEffects.push(createHorsesDroppedUpdateAction(game.horses));
          batchedEffects.push(createHorsesFinishedUpdateAction(game.horses));
          batchedEffects.push(put(setHorsesPoints(game.horses.map((horse) => horse.points))));
          batchedEffects.push(put(setTurnOrder(game.turnorder)));
          batchedEffects.push(put(setTurnPos(game.turnpos)));
          if (game.turntype === Turfmaster.turntypes.CARDS)
            batchedEffects.push(put(setHandiFailureHorseIndex(-1)));
          // In dice rounds, keep last card visible through first dice turn only.
          // Once first dice player has completed their move, server advances turnpos > 0.
          // Avoid race: controller snapshot can already report CARDS due to server advancing
          // turntype right after emitting ENDTURN. Use previous Redux turnType.
          const effectivePrevTurnType =
            typeof prevTurnType === 'number' ? prevTurnType : game.turntype;
          if (effectivePrevTurnType === Turfmaster.turntypes.DICES && game.turnpos > 0) {
            batchedEffects.push(put(setLastCardHorseIndex(-1)));
            batchedEffects.push(put(setLastCard(-1)));
          }
          break;
        }
        case Turfmaster.stateMachine.ENDROUND:
          directionalGameLog.inbound(
            'ENDROUND',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(put(setTurnType(game.turntype)));
          batchedEffects.push(put(setTurnDicePlayerId(game.turndiceplayerid)));
          batchedEffects.push(put(setLastCardHorseIndex(-1)));
          batchedEffects.push(put(setLastCard(-1)));
          batchedEffects.push(put(setEventLog([...eventLog])));
          break;
        case Turfmaster.stateMachine.ENDGAME:
          directionalGameLog.inbound(
            'ENDGAME',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          {
            const horsesFieldEffect = createHorsesFieldUpdateAction(game.horses);
            if (horsesFieldEffect) batchedEffects.push(horsesFieldEffect);
          }
          batchedEffects.push(createHorsesRanksUpdateAction(game.horses));
          batchedEffects.push(createPlayersUpdateAction(game.players, game.horses));
          batchedEffects.push(createHorsesHandiUpdateAction(game.horses));
          batchedEffects.push(put(setTurnOrder(game.turnorder)));
          batchedEffects.push(put(setTurnPos(game.turnpos)));
          batchedEffects.push(put(setRound(game.round)));
          batchedEffects.push(put(setTurnType(game.turntype)));
          batchedEffects.push(put(setEventLog([...eventLog])));
          break;
        case Turfmaster.stateMachine.NEWMESSAGE:
          directionalGameLog.inbound(
            'NEWMESSAGE',
            {
              phase: 'response',
              payloadSummary: summarizeControllerContext({
                controller,
                state,
                params,
                game,
                board,
              }),
            },
            'controller event',
          );
          batchedEffects.push(put(setEventLog([...eventLog])));
          break;
        default:
          gameLogger.warn({ state, params, game, board }, 'unknown controller event');
          break;
      }

      if (shouldSetCurrentStateFromEvent) {
        if (state === Turfmaster.stateMachine.STARTGAME && params?.currentState != null) {
          // STARTGAME case already dispatched setCurrentState(params.currentState) after board sync.
        } else {
          batchedEffects.push(put(setCurrentState(state)));
        }
      }

      // Gate: during history mode do not apply game/board updates so historical view is not overwritten.
      const isHistoryMode: boolean = yield select(selectIsHistoryMode);
      if (!isHistoryMode) {
        yield all(batchedEffects);
      }

      // Always keep eventLog and anchor fresh so Return to Live has current state
      yield put(setEventLog([...eventLog]));
      // Use cloneDeep with fallback to gameToSerializable for robustness
      // cloneDeep handles undefined values, Infinity/NaN, and circular references better than JSON.stringify
      let gameAnchor: Record<string, unknown>;
      try {
        gameAnchor = gameToSerializable(game);
      } catch (error) {
        gameLogger.warn({ err: error }, 'gameToSerializable failed, falling back to cloneDeep');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        gameAnchor = cloneDeep(game) as any;
      }
      yield put(setLatestGameStateAnchor(gameAnchor));
    } catch (error) {
      // Log error but continue processing next event to prevent silent saga death
      gameLogger.error(
        { err: error, state, params },
        'error processing controller event in sagaGameReceiver',
      );
    }
  }
}

export default sagaGameReceiver;
